import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  PackageCheck, Plus, Clock, CheckCircle, XCircle, RefreshCw, Calendar,
  AlertCircle, Truck, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { createDemobRequest, getDemobList, cancelDemob, getDemobAudit } from '../lib/api';
import api from '../lib/api';

const STATUS_LABEL = {
  requested: { label: 'Awaiting Operations', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
  scheduled: { label: 'Collection Scheduled', className: 'bg-blue-100 text-blue-800', icon: Calendar },
  in_progress: { label: 'Under Inspection', className: 'bg-purple-100 text-purple-800', icon: ShieldCheck },
  pending_invoice: { label: 'Pending Final Invoice', className: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-700', icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_LABEL[status] || { label: status, className: 'bg-gray-100 text-gray-700', icon: Clock };
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.className} inline-flex items-center gap-1`} data-testid={`demob-status-${status}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
};

const NewRequestDialog = ({ open, onOpenChange, onCreated }) => {
  const [availableDNs, setAvailableDNs] = useState([]);
  const [loadingDNs, setLoadingDNs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    delivery_note_id: '',
    trigger: 'client_request',
    requested_return_date: new Date().toISOString().slice(0, 10),
    return_location: '',
    transport_required: false,
    transport_notes: '',
    client_contact_name: '',
    client_contact_phone: '',
    reason: '',
    notes: '',
  });

  const loadDNs = useCallback(async () => {
    try {
      setLoadingDNs(true);
      // Scoped endpoint — RBAC ensures Sales sees only their own DNs
      const res = await api.get('/rbac/my-delivery-notes');
      const eligible = (res.data || []).filter(dn =>
        ['delivered', 'acknowledged'].includes(dn.status) && !dn.demob_in_progress
      );
      setAvailableDNs(eligible);
    } catch (err) {
      toast.error('Failed to load your delivery notes');
    } finally {
      setLoadingDNs(false);
    }
  }, []);

  useEffect(() => { if (open) loadDNs(); }, [open, loadDNs]);

  const handleSubmit = async () => {
    if (!form.delivery_note_id) return toast.error('Select a delivery note first');
    if (!form.reason?.trim()) return toast.error('Reason is required');
    try {
      setSubmitting(true);
      await createDemobRequest(form);
      toast.success('Demobilization request submitted. Operations will schedule collection.');
      onCreated?.();
      onOpenChange(false);
      setForm({
        delivery_note_id: '',
        trigger: 'client_request',
        requested_return_date: new Date().toISOString().slice(0, 10),
        return_location: '', transport_required: false, transport_notes: '',
        client_contact_name: '', client_contact_phone: '', reason: '', notes: '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-demob-request-dialog">
        <DialogHeader>
          <DialogTitle>New Demobilization Request</DialogTitle>
          <DialogDescription>
            Submit a request to return rental equipment. Operations will review, schedule collection and complete inspection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Delivery Note *</Label>
            <Select
              value={form.delivery_note_id}
              onValueChange={(v) => setForm({ ...form, delivery_note_id: v })}
              disabled={loadingDNs}
            >
              <SelectTrigger data-testid="demob-select-dn">
                <SelectValue placeholder={loadingDNs ? 'Loading…' : 'Choose one of your delivered DNs'} />
              </SelectTrigger>
              <SelectContent>
                {availableDNs.length === 0 && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No eligible DNs (delivered & not already off-hire)</div>
                )}
                {availableDNs.map(dn => (
                  <SelectItem key={dn.id} value={dn.id} data-testid={`dn-option-${dn.dn_number}`}>
                    {dn.dn_number} — {dn.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Trigger</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger data-testid="demob-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_request">Client Request</SelectItem>
                  <SelectItem value="contract_expiry">Contract Expiry</SelectItem>
                  <SelectItem value="early_return">Early Return</SelectItem>
                  <SelectItem value="admin_action">Admin Action</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Requested Return Date *</Label>
              <Input
                type="date"
                lang="en-GB"
                value={form.requested_return_date}
                onChange={(e) => setForm({ ...form, requested_return_date: e.target.value })}
                data-testid="demob-return-date"
              />
            </div>
          </div>

          <div>
            <Label>Return Location</Label>
            <Input
              value={form.return_location}
              onChange={(e) => setForm({ ...form, return_location: e.target.value })}
              placeholder="Where should Operations collect the equipment?"
              data-testid="demob-return-location"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client Contact Name</Label>
              <Input
                value={form.client_contact_name}
                onChange={(e) => setForm({ ...form, client_contact_name: e.target.value })}
                data-testid="demob-contact-name"
              />
            </div>
            <div>
              <Label>Client Contact Phone</Label>
              <Input
                value={form.client_contact_phone}
                onChange={(e) => setForm({ ...form, client_contact_phone: e.target.value })}
                data-testid="demob-contact-phone"
              />
            </div>
          </div>

          <div>
            <Label>Reason *</Label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Why is the client returning the equipment?"
              rows={2}
              data-testid="demob-reason"
            />
          </div>

          <div>
            <Label>Additional Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              data-testid="demob-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-demob-request-btn">
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatusTimelineDialog = ({ open, onOpenChange, demob }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !demob) return;
    (async () => {
      try {
        setLoading(true);
        const res = await getDemobAudit(demob.id);
        setEntries(res.data || []);
      } catch (err) {
        toast.error('Failed to load status timeline');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, demob]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="demob-audit-dialog">
        <DialogHeader>
          <DialogTitle>Request Status – {demob?.demob_number}</DialogTitle>
          <DialogDescription>Complete history of your demobilization request (read-only).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="text-sm text-muted-foreground">No activity yet. Waiting for Operations.</div>
          )}
          {entries.map((e) => (
            <div key={e.id} className="border-l-2 border-slate-200 pl-4 py-1" data-testid={`audit-${e.action}`}>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium capitalize">{(e.action || '').replace(/_/g, ' ')}</span>
                {e.new_status && <StatusBadge status={e.new_status} />}
              </div>
              <div className="text-xs text-muted-foreground">
                {new Date(e.timestamp).toLocaleString()} · {e.user_email} ({e.user_role})
              </div>
              {e.remarks && <div className="text-xs mt-1 text-slate-700">“{e.remarks}”</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RequestsTable = ({ rows, onView, onCancel }) => {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground py-8 text-center">No demobilization requests yet.</div>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Number</TableHead>
          <TableHead>DN</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Requested Return</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((d) => (
          <TableRow key={d.id} data-testid={`demob-row-${d.demob_number}`}>
            <TableCell className="font-mono text-xs">{d.demob_number}</TableCell>
            <TableCell className="font-mono text-xs">{d.dn_number}</TableCell>
            <TableCell>{d.client_name}</TableCell>
            <TableCell>{d.requested_return_date || '—'}</TableCell>
            <TableCell><StatusBadge status={d.status} /></TableCell>
            <TableCell className="text-right space-x-2">
              <Button size="sm" variant="outline" onClick={() => onView(d)} data-testid={`view-demob-${d.demob_number}`}>Status</Button>
              {d.status === 'requested' && (
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => onCancel(d)} data-testid={`cancel-demob-${d.demob_number}`}>Cancel</Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const SalesDemobRequestsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedDemob, setSelectedDemob] = useState(null);
  const [tab, setTab] = useState('mine');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Sales users are auto-scoped by backend RBAC to their own requests.
      const res = await getDemobList({});
      setRows(res.data || []);
    } catch (err) {
      toast.error('Failed to load your demobilization requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openStatus = (d) => { setSelectedDemob(d); setAuditOpen(true); };
  const handleCancel = async (d) => {
    if (!window.confirm(`Cancel demobilization request ${d.demob_number}?`)) return;
    try {
      await cancelDemob(d.id, 'Cancelled by requester');
      toast.success('Request cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const active = rows.filter(r => !['completed', 'cancelled'].includes(r.status));
  const closed = rows.filter(r => ['completed', 'cancelled'].includes(r.status));
  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="sales-demob-requests-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <PackageCheck className="h-7 w-7" /> Demobilization Requests
          </h1>
          <p className="text-muted-foreground">Submit a return request for one of your delivered equipment and track its status.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-demob-btn"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="open-new-demob-btn">
            <Plus className="h-4 w-4 mr-2" /> New Request
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Awaiting Ops</div><div className="text-2xl font-bold">{counts.requested || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Scheduled</div><div className="text-2xl font-bold text-blue-700">{counts.scheduled || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Under Inspection</div><div className="text-2xl font-bold text-purple-700">{counts.in_progress || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Completed</div><div className="text-2xl font-bold text-green-700">{counts.completed || 0}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">My Requests</CardTitle>
          <CardDescription>You can only view requests you created. Operations owns the collection and inspection.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="mine" data-testid="tab-active">Active ({active.length})</TabsTrigger>
              <TabsTrigger value="closed" data-testid="tab-closed">Closed ({closed.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="mine" className="mt-4">
              {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
                <RequestsTable rows={active} onView={openStatus} onCancel={handleCancel} />
              )}
            </TabsContent>
            <TabsContent value="closed" className="mt-4">
              {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
                <RequestsTable rows={closed} onView={openStatus} onCancel={handleCancel} />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <NewRequestDialog open={newOpen} onOpenChange={setNewOpen} onCreated={fetchData} />
      <StatusTimelineDialog open={auditOpen} onOpenChange={setAuditOpen} demob={selectedDemob} />
    </div>
  );
};

export default SalesDemobRequestsPage;
